import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  Ban,
  CheckCircle2,
  Circle,
  CreditCard,
  Mail,
  MapPin,
  MessageSquare,
  MessageSquareLock,
  PackageCheck,
  PackagePlus,
  PencilLine,
  PhoneCall,
  RotateCcw,
  ShoppingBag,
  StickyNote,
  Tag,
  Truck,
  Undo2,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import {
  CALL_ATTEMPT_OUTCOME_LABELS,
  ORDER_STAGE_LABELS,
  type CallAttempt,
  type Comment,
  type Json,
  type OrderChannelEvent,
  type OrderEvent,
  type OrderEventType,
  type OrderStage,
} from "@estelle/domain";

import { plain } from "@/modules/orders/plain";
import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { CommentBody } from "@/modules/comments/CommentBody";
import { commentKeys } from "@/modules/comments/keys";
import { callKeys } from "@/modules/orders/calls";
import { statusLabel } from "@/modules/orders/changeText";
import { eventValue } from "@/modules/orders/format";

const TYPE_LABEL: Record<OrderEventType, string> = {
  created: "Order placed",
  tag_added: "Tag added",
  tag_removed: "Tag removed",
  note_changed: "Note changed",
  financial_status_changed: "Payment status changed",
  fulfilment_status_changed: "Fulfilment status changed",
  shipment_created: "Shipment created",
  shipment_updated: "Shipment updated",
  cancelled: "Cancelled",
  refunded: "Refunded",
  address_changed: "Address changed",
  line_item_changed: "Line item changed",
  rto_received: "Return received",
  possible_double_restock: "Possible double restock",
  return_added_manually: "Return added by hand",
  stage_override_set: "Stage set by hand",
  stage_override_cleared: "Stage back to automatic",
};

type Tone = "neutral" | "primary" | "success" | "warning" | "danger";

const TONE: Record<Tone, string> = {
  neutral: "bg-muted text-muted-foreground",
  primary: "bg-primary/15 text-primary-strong",
  success: "bg-tint-green text-tint-green-foreground",
  warning: "bg-tint-yellow text-tint-yellow-foreground",
  danger: "bg-tint-red text-tint-red-foreground",
};

function field(value: Json | null, key: string): string | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const inner = value[key];
  return typeof inner === "string" ? inner : null;
}

/**
 * The icon, colour and sentence for one event.
 *
 * The event carries `before`/`after` as JSON, shaped by whoever wrote it —
 * ingestion writes `{ shipment, status }` for a shipment, a bare string for a
 * tag. Anything this does not recognise falls back to the raw values, so a new
 * event shape shows up plainly rather than disappearing.
 */
function present(event: OrderEvent): {
  icon: LucideIcon;
  tone: Tone;
  title: string;
  detail: ReactNode;
} {
  const title = TYPE_LABEL[event.eventType];

  switch (event.eventType) {
    case "created":
      return {
        icon: ShoppingBag,
        tone: "primary",
        title,
        detail: field(event.after, "orderNumber"),
      };
    case "tag_added":
    case "tag_removed":
      return {
        icon: Tag,
        tone: "neutral",
        title,
        detail: (
          <Badge
            variant="outline"
            className={cn(
              "font-normal",
              event.eventType === "tag_added"
                ? "border-primary/40 bg-primary/10 text-primary-strong"
                : "text-muted-foreground line-through",
            )}
          >
            {eventValue(
              event.eventType === "tag_added" ? event.after : event.before,
            )}
          </Badge>
        ),
      };
    case "note_changed":
      return {
        icon: StickyNote,
        tone: "neutral",
        title,
        detail: eventValue(event.after),
      };
    case "financial_status_changed":
    case "fulfilment_status_changed": {
      const after =
        typeof event.after === "string" || event.after === null
          ? event.after
          : null;
      const before =
        typeof event.before === "string" || event.before === null
          ? event.before
          : null;
      return {
        icon:
          event.eventType === "financial_status_changed"
            ? CreditCard
            : PackageCheck,
        tone: after === "paid" || after === "fulfilled" ? "success" : "neutral",
        title,
        detail: `${statusLabel(before)} → ${statusLabel(after)}`,
      };
    }
    case "shipment_created": {
      const parts = [
        field(event.after, "shipment"),
        field(event.after, "courier"),
      ];
      return {
        icon: PackagePlus,
        tone: "primary",
        title,
        detail: parts.filter(Boolean).join(" · ") || null,
      };
    }
    case "shipment_updated": {
      const shipment = field(event.after, "shipment");
      const status = field(event.after, "status");
      if (event.field === "status" && status) {
        const previous = field(event.before, "status");
        return {
          icon:
            status === "delivered"
              ? CheckCircle2
              : status === "not_delivered" ||
                  status === "attempted_delivery" ||
                  status === "failed"
                ? AlertTriangle
                : Truck,
          tone:
            status === "delivered"
              ? "success"
              : status === "not_delivered" ||
                  status === "attempted_delivery" ||
                  status === "failed"
                ? "warning"
                : status === "cancelled"
                  ? "danger"
                  : "neutral",
          title: statusLabel(status),
          detail: [
            shipment,
            previous
              ? `${statusLabel(previous)} → ${statusLabel(status)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · "),
        };
      }
      return {
        icon: Truck,
        tone: "neutral",
        title: event.field === "tracking" ? "Tracking updated" : title,
        detail: shipment,
      };
    }
    case "cancelled": {
      const reason = field(event.after, "reason");
      return {
        icon: Ban,
        tone: "danger",
        title,
        detail: reason ? `Reason: ${reason.toLowerCase()}` : null,
      };
    }
    case "refunded":
      return { icon: Undo2, tone: "danger", title, detail: null };
    case "address_changed":
      return {
        icon: MapPin,
        tone: "neutral",
        title,
        detail: "Shipping address updated",
      };
    case "line_item_changed":
      return {
        icon: PencilLine,
        tone: "neutral",
        title,
        detail: `${eventValue(event.before)} → ${eventValue(event.after)}`,
      };
    // The team received a returned parcel (#529): what was counted, and where
    // the good units went back.
    case "rto_received": {
      const count = (key: string) => {
        const value =
          event.after !== null &&
          typeof event.after === "object" &&
          !Array.isArray(event.after)
            ? event.after[key]
            : undefined;
        return typeof value === "number" ? value : null;
      };
      const parts = [
        field(event.after, "shipment"),
        count("good") !== null ? `${String(count("good"))} good` : null,
        (count("damaged") ?? 0) > 0
          ? `${String(count("damaged"))} damaged`
          : null,
        (count("missing") ?? 0) > 0
          ? `${String(count("missing"))} missing`
          : null,
        field(event.after, "warehouse"),
      ];
      return {
        icon: PackageCheck,
        tone: "success",
        title,
        detail: parts.filter(Boolean).join(" · ") || null,
      };
    }
    // A person added a return by hand (#594).
    case "return_added_manually": {
      const reason = field(event.after, "reason");
      const shipment = field(event.after, "shipment");
      return {
        icon: RotateCcw,
        tone: "neutral",
        title,
        detail: [shipment, reason ? `Reason: ${reason}` : null]
          .filter(Boolean)
          .join(" · "),
      };
    }
    // A person set the stage by hand, or put it back to automatic (#591).
    case "stage_override_set": {
      const stage = field(event.after, "stage");
      const reason = field(event.after, "reason");
      return {
        icon: PencilLine,
        tone: "neutral",
        title: stage
          ? `Stage set to ${ORDER_STAGE_LABELS[stage as OrderStage] ?? stage}`
          : title,
        detail: reason ? `Reason: ${reason}` : null,
      };
    }
    case "stage_override_cleared": {
      const stage = field(event.after, "stage");
      return {
        icon: RotateCcw,
        tone: "neutral",
        title,
        detail: stage
          ? `Tracking says ${ORDER_STAGE_LABELS[stage as OrderStage] ?? stage}`
          : null,
      };
    }
    // Shopify restocked an order the ERP had already received (#529).
    case "possible_double_restock": {
      const quantity =
        event.after !== null &&
        typeof event.after === "object" &&
        !Array.isArray(event.after) &&
        typeof event.after.restockedQuantity === "number"
          ? event.after.restockedQuantity
          : null;
      return {
        icon: AlertTriangle,
        tone: "warning",
        title,
        detail:
          quantity === null
            ? "Shopify restocked this order after the ERP had."
            : `Shopify restocked ${String(quantity)} unit${quantity === 1 ? "" : "s"} after the ERP had received the return.`,
      };
    }
  }
}

/** The shop's calendar day — IST, the same boundary every date in the app uses. */
const DAY_KEY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const DAY_LABEL = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});
const TIME = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "numeric",
  minute: "2-digit",
});

/** One row of the merged timeline, whichever system recorded it. */
interface Entry {
  id: string;
  occurredAt: string;
  icon: LucideIcon;
  tone: Tone;
  title: ReactNode;
  detail: ReactNode;
  by: string;
  group: "comment" | "shopify" | "erp" | "erp_comment" | "calls";
}

function channelIcon(action: string | null): { icon: LucideIcon; tone: Tone } {
  const a = action ?? "";
  if (a === "mail_sent") return { icon: Mail, tone: "neutral" };
  if (a === "placed") return { icon: ShoppingBag, tone: "primary" };
  if (a === "confirmed" || a === "confirmation_number_generated")
    return { icon: CheckCircle2, tone: "neutral" };
  if (a.startsWith("fulfillment_order"))
    return { icon: PackageCheck, tone: "neutral" };
  if (a === "fulfillment_cancelled") return { icon: Ban, tone: "danger" };
  if (a === "fulfillment_marked_as_delivered")
    return { icon: CheckCircle2, tone: "success" };
  if (a.startsWith("fulfillment")) return { icon: Truck, tone: "primary" };
  if (a.startsWith("refund")) return { icon: Undo2, tone: "danger" };
  if (a.startsWith("return")) return { icon: RotateCcw, tone: "warning" };
  if (a === "cancelled" || a === "payment_cancel_success")
    return { icon: Ban, tone: "danger" };
  if (a === "closed") return { icon: Archive, tone: "neutral" };
  if (a === "edited" || a === "changed")
    return { icon: PencilLine, tone: "neutral" };
  if (/sale|capture|payment|authorization/.test(a))
    return {
      icon: CreditCard,
      tone: a.endsWith("success") ? "success" : "neutral",
    };
  return { icon: Circle, tone: "neutral" };
}

/**
 * ERP events a Shopify timeline already tells, in Shopify's own words. Built
 * from the payload's timestamps on first sight; with Shopify's events present
 * they would say "placed" and "created" for the same moment.
 */
const COVERED_BY_CHANNEL = new Set<OrderEventType>([
  "created",
  "shipment_created",
  "cancelled",
  "refunded",
]);

/**
 * The team's own comments on the order (#448), as timeline rows. Marked "ERP
 * only" on the row itself, not just in a legend: they are the one thing here
 * that is never in Shopify, and the Shopify comments beside them look alike. A
 * deleted comment is a row too, with nothing in it but that it was deleted.
 */
function commentEntries(comments: Comment[]): Entry[] {
  const erpOnly = (
    <Badge variant="outline" className="ml-1.5 gap-1 align-middle font-normal">
      ERP only
    </Badge>
  );
  return comments.map((comment): Entry => {
    if (comment.deletedAt !== null) {
      return {
        id: `cm-${comment.id}`,
        occurredAt: comment.deletedAt,
        icon: MessageSquareLock,
        tone: "neutral",
        title: <>A comment was deleted{erpOnly}</>,
        detail: null,
        by: `Comment by ${comment.authorName} · never sent to Shopify`,
        group: "erp_comment",
      };
    }
    return {
      id: `cm-${comment.id}`,
      occurredAt: comment.createdAt,
      icon: MessageSquareLock,
      tone: "primary",
      title: (
        <>
          {comment.authorName} commented{erpOnly}
        </>
      ),
      detail: (
        <div className="bg-muted/60 text-foreground mt-1 space-y-1 rounded-md px-3 py-2">
          {comment.body !== "" ? (
            <CommentBody
              body={comment.body}
              mentions={comment.mentions}
              className="line-clamp-4"
            />
          ) : null}
          {comment.attachments.length > 0 ? (
            <p className="text-muted-foreground text-xs">
              {comment.attachments.length} file
              {comment.attachments.length === 1 ? "" : "s"} attached
            </p>
          ) : null}
        </div>
      ),
      by: "Comment in this ERP · never sent to Shopify",
      group: "erp_comment",
    };
  });
}

/**
 * Confirmation calls (#636) as timeline rows: who pressed Call, and the
 * outcome they gave, if they gave one. A row says a call was started, not
 * that it connected.
 */
function callEntries(attempts: CallAttempt[]): Entry[] {
  return attempts.map((attempt): Entry => {
    const who = attempt.userName ?? "Someone";
    return {
      id: `call-${attempt.id}`,
      occurredAt: attempt.startedAt,
      icon: PhoneCall,
      tone:
        attempt.outcome === "confirmed"
          ? "success"
          : attempt.outcome === "no_answer"
            ? "warning"
            : "neutral",
      title: `${who} called the customer`,
      detail: attempt.outcome
        ? CALL_ATTEMPT_OUTCOME_LABELS[attempt.outcome]
        : "Outcome not given",
      by: "Call started in this ERP",
      group: "calls",
    };
  });
}

function entries(
  events: OrderEvent[],
  channelEvents: OrderChannelEvent[],
  comments: Comment[],
  attempts: CallAttempt[] = [],
): Entry[] {
  const hasChannel = channelEvents.length > 0;
  const ours: Entry[] = events
    .filter(
      (event) =>
        !(
          hasChannel &&
          event.source === "channel" &&
          COVERED_BY_CHANNEL.has(event.eventType)
        ),
    )
    .map((event) => {
      const { icon, tone, title, detail } = present(event);
      return {
        id: `erp-${event.id}`,
        occurredAt: event.occurredAt,
        icon,
        tone,
        title,
        detail,
        by:
          event.source === "erp"
            ? `ERP · user ${event.actorUserId ?? "unknown"}`
            : "Detected by the ERP from a Shopify sync",
        group: "erp",
      };
    });

  const theirs: Entry[] = channelEvents.map((event) => {
    if (event.kind === "comment") {
      return {
        id: `ch-${event.id}`,
        occurredAt: event.occurredAt,
        icon: MessageSquare,
        tone: "primary",
        title: event.author ?? "Staff comment",
        detail: (
          <p className="bg-muted/60 text-foreground mt-1 rounded-md px-3 py-2 whitespace-pre-line">
            {plain(event.message)}
          </p>
        ),
        by: "Comment in Shopify",
        group: "comment",
      };
    }
    const { icon, tone } = channelIcon(event.action);
    return {
      id: `ch-${event.id}`,
      occurredAt: event.occurredAt,
      icon,
      tone: event.critical ? "warning" : tone,
      title: plain(event.message) || (event.action ?? "Event"),
      detail: null,
      by: event.appTitle ?? "Shopify",
      group: "shopify",
    };
  });

  return [
    ...ours,
    ...theirs,
    ...commentEntries(comments),
    ...callEntries(attempts),
  ].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

function groupByDay(list: Entry[]) {
  const days: { key: string; label: string; entries: Entry[] }[] = [];
  for (const entry of list) {
    const at = new Date(entry.occurredAt);
    const key = DAY_KEY.format(at);
    const last = days[days.length - 1];
    if (last?.key === key) {
      last.entries.push(entry);
    } else {
      days.push({ key, label: DAY_LABEL.format(at), entries: [entry] });
    }
  }
  return days;
}

const GROUP_LABEL: Record<Entry["group"], string> = {
  comment: "Shopify comments",
  shopify: "Shopify events",
  erp: "ERP changes",
  erp_comment: "ERP comments",
  calls: "Calls",
};

/**
 * One order's timeline (#56, FR-ORD-13, #327): Shopify's own events and staff
 * comments merged with the changes the ERP detects by diffing each sync.
 *
 * Newest first, grouped by IST day. Each ERP change is its own row — a tag
 * added and a tag removed are two rows (FR-ORD-12). Filterable by where the
 * entry came from. Read-only: posting a Shopify comment is a Shopify write (X1).
 *
 * The team's ERP-only comments (#448) are on it too, marked as such — written
 * in the Comments card above, read here from the same cached thread so the two
 * cannot disagree. A failed read of them leaves the rest of the timeline alone.
 */
export function OrderHistory({
  orderId,
  channelEvents = [],
}: {
  orderId: string;
  channelEvents?: OrderChannelEvent[];
}) {
  const data = useDataSource();
  const [group, setGroup] = useState<Entry["group"] | null>(null);

  const query = useQuery({
    queryKey: ["orders", "history", orderId],
    queryFn: () => data.orders.history(orderId, { limit: 100 }),
  });

  const commentsQuery = useQuery({
    queryKey: commentKeys.thread("order", orderId),
    queryFn: () => data.comments.list("order", orderId),
  });

  // The call log (#636), from the same cache entry the confirm sheet reads.
  const callsQuery = useQuery({
    queryKey: callKeys.order(orderId),
    queryFn: () => data.orders.callAttempts(orderId),
  });

  const all = entries(
    query.data?.items ?? [],
    channelEvents,
    commentsQuery.data?.items ?? [],
    callsQuery.data ?? [],
  );
  const groups = [...new Set(all.map((entry) => entry.group))];
  const shown = group ? all.filter((entry) => entry.group === group) : all;

  return (
    <Card>
      <CardHeader className="gap-3">
        <CardTitle className="text-sm">Timeline</CardTitle>
        {groups.length > 1 ? (
          <div
            role="radiogroup"
            aria-label="Filter the timeline"
            className="flex flex-wrap gap-1.5"
          >
            {[null, ...groups].map((option) => {
              const active = group === option;
              const count = option
                ? all.filter((entry) => entry.group === option).length
                : all.length;
              return (
                <button
                  key={option ?? "all"}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => {
                    setGroup(option);
                  }}
                  className={cn(
                    "focus-visible:ring-ring rounded-full border px-2.5 py-0.5 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none",
                    active
                      ? "border-primary bg-primary/10 text-primary-strong font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option ? GROUP_LABEL[option] : "All"} ({count})
                </button>
              );
            })}
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <TableSkeleton rows={3} />
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : shown.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No recorded changes for this order.
          </p>
        ) : (
          <div className="space-y-5">
            {groupByDay(shown).map((day) => (
              <section key={day.key} aria-label={day.label}>
                <h3 className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
                  {day.label}
                </h3>
                <ol className="relative">
                  {day.entries.map((entry, index) => {
                    const Icon = entry.icon;
                    const last = index === day.entries.length - 1;
                    return (
                      <li
                        key={entry.id}
                        className="relative flex gap-3 pb-4 last:pb-0"
                      >
                        {!last ? (
                          <span
                            aria-hidden
                            className="bg-border absolute top-8 bottom-0 left-[15px] w-px"
                          />
                        ) : null}
                        <span
                          aria-hidden
                          className={cn(
                            "relative flex size-8 shrink-0 items-center justify-center rounded-full",
                            TONE[entry.tone],
                          )}
                        >
                          <Icon className="size-4" />
                        </span>
                        <div className="min-w-0 flex-1 pt-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="min-w-0 text-sm font-medium">
                              {entry.title}
                            </p>
                            <time
                              dateTime={entry.occurredAt}
                              title={`${DAY_LABEL.format(new Date(entry.occurredAt))}, ${TIME.format(new Date(entry.occurredAt))} IST`}
                              className="text-muted-foreground shrink-0 text-xs tabular-nums"
                            >
                              {TIME.format(new Date(entry.occurredAt))}
                            </time>
                          </div>
                          {entry.detail ? (
                            <div className="text-muted-foreground mt-0.5 text-sm">
                              {entry.detail}
                            </div>
                          ) : null}
                          <p className="text-muted-foreground mt-1 text-xs">
                            {entry.by}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
